/**
 * Inventory and Vendor API Service
 * Handles all API calls for inventory and vendor management
 */
import { API_BASE } from '../bootstrap/patchApiBase';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_API_BASE_URL?.replace(/\/api$/, '/api/v1') ||
  `${API_BASE}/api/v1`;

interface ApiResponse<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

/**
 * Get auth token from localStorage
 */
const getAuthToken = (): string | null => {
  return sessionStorage.getItem('access_token');
};

/**
 * Get site code from localStorage or params
 */
const getSiteCode = (site?: string): string => {
  const normalize = (value?: string | null): string | null => {
    const text = String(value || '').trim();
    if (!text || ['undefined', 'null', 'none', 'nan'].includes(text.toLowerCase())) {
      return null;
    }
    return text.toUpperCase();
  };

  return (
    normalize(site) ||
    normalize(sessionStorage.getItem('tenant')) ||
    normalize(localStorage.getItem('currentSite')) ||
    'ADMIN'
  );
};

/**
 * Generic fetch with auth and site headers
 */
const fetchApi = async <T = any>(
  endpoint: string,
  options?: RequestInit,
  siteCode?: string,
  timeoutMs: number = 15000
): Promise<ApiResponse<T>> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const token = getAuthToken();
    const site = getSiteCode(siteCode);

    const headers: Record<string, string> = {
      'X-Site-Code': site
    };

    const isFormData = options?.body instanceof FormData;
    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Merge with existing headers if any
    if (options?.headers) {
      Object.assign(headers, options.headers);
    }

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      signal: controller.signal
    });

    clearTimeout(timeout);

    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');

    let data = null;
    if (isJson) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      error: response.ok ? null : `HTTP ${response.status}: ${data?.message || data?.error || 'Unknown error'}`
    };
  } catch (error: any) {
    clearTimeout(timeout);
    const errorMessage = error.name === 'AbortError'
      ? 'Connection timeout - server may not be running'
      : error.message || 'Network error';

    return {
      ok: false,
      status: 0,
      data: null,
      error: errorMessage
    };
  }
};

/**
 * Inventory API
 */
export const inventoryApi = {
  /**
   * Get all inventory items
   */
  getAll: async (siteCode?: string, params?: { search?: string; category?: string; status?: string }): Promise<ApiResponse<any[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.category) queryParams.append('category', params.category);
    if (params?.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    const endpoint = query ? `/inventory/?${query}` : '/inventory/';
    return fetchApi(endpoint, { method: 'GET' }, siteCode);
  },

  /**
   * Get inventory statistics
   */
  getStats: async (siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi('/inventory/stats/', { method: 'GET' }, siteCode);
  },

  /**
   * Get inventory item by ID
   */
  getById: async (id: number, siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi(`/inventory/${id}/`, { method: 'GET' }, siteCode);
  },

  /**
   * Create inventory item
   */
  create: async (item: any, siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi('/inventory/', {
      method: 'POST',
      body: JSON.stringify({ ...item, site_code: getSiteCode(siteCode) })
    }, siteCode);
  },

  /**
   * Update inventory item
   */
  update: async (id: number, item: any, siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi(`/inventory/${id}/`, {
      method: 'PUT',
      body: JSON.stringify({ ...item, site_code: getSiteCode(siteCode) })
    }, siteCode);
  },

  /**
   * Delete inventory item
   */
  delete: async (id: number, siteCode?: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/inventory/${id}/`, { method: 'DELETE' }, siteCode);
  },

  /**
   * Get inventory item history
   */
  getHistory: async (id: number, siteCode?: string): Promise<ApiResponse<any[]>> => {
    return fetchApi(`/inventory/${id}/history/`, { method: 'GET' }, siteCode);
  },

  /**
   * Bulk upload inventory items from Excel/CSV
   */
  bulkUpload: async (file: File, siteCode?: string): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_code', getSiteCode(siteCode));

    return fetchApi('/inventory/bulk_upload/', {
      method: 'POST',
      body: formData
    }, siteCode);
  }
};

/**
 * Vendor API
 */
export const vendorApi = {
  /**
   * Get all vendors
   */
  getAll: async (siteCode?: string, params?: { search?: string; status?: string }): Promise<ApiResponse<any[]>> => {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    const endpoint = query ? `/vendors/?${query}` : '/vendors/';
    return fetchApi(endpoint, { method: 'GET' }, siteCode);
  },

  /**
   * Get vendor statistics
   */
  getStats: async (siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi('/vendors/stats/', { method: 'GET' }, siteCode);
  },

  /**
   * Get vendor by ID
   */
  getById: async (id: number, siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi(`/vendors/${id}/`, { method: 'GET' }, siteCode);
  },

  /**
   * Create vendor
   */
  create: async (vendor: any, siteCode?: string): Promise<ApiResponse<any>> => {
    let body;
    if (vendor instanceof FormData) {
      vendor.append('site_code', getSiteCode(siteCode));
      body = vendor;
    } else {
      body = JSON.stringify({ ...vendor, site_code: getSiteCode(siteCode) });
    }

    return fetchApi('/vendors/', {
      method: 'POST',
      body
    }, siteCode);
  },

  /**
   * Update vendor
   */
  update: async (id: number, vendor: any, siteCode?: string): Promise<ApiResponse<any>> => {
    return fetchApi(`/vendors/${id}/`, {
      method: 'PUT',
      body: JSON.stringify({ ...vendor, site_code: getSiteCode(siteCode) })
    }, siteCode);
  },

  /**
   * Delete vendor
   */
  delete: async (id: number, siteCode?: string): Promise<ApiResponse<void>> => {
    return fetchApi(`/vendors/${id}/`, { method: 'DELETE' }, siteCode);
  },

  /**
   * Bulk upload vendors from Excel/CSV
   */
  bulkUpload: async (file: File, siteCode?: string, uploadMode: 'append' | 'upsert' = 'append'): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_code', getSiteCode(siteCode));
    formData.append('upload_mode', uploadMode);

    return fetchApi('/vendors/bulk_upload/', {
      method: 'POST',
      body: formData
    }, siteCode);
  },

  /**
   * Bulk upload invoices
   */
  bulkUploadInvoices: async (file: File, siteCode?: string): Promise<ApiResponse<any>> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('site_code', getSiteCode(siteCode));

    return fetchApi('/vendors/invoices/bulk_upload/', {
      method: 'POST',
      body: formData
    }, siteCode);
  },

  /**
   * Add invoice to vendor
   */
  addInvoice: async (vendorId: number, invoiceData: FormData, siteCode?: string): Promise<ApiResponse<any>> => {
    invoiceData.append('site_code', getSiteCode(siteCode));
    return fetchApi(`/vendors/${vendorId}/invoices/`, {
      method: 'POST',
      body: invoiceData
    }, siteCode);
  }
};

